import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

import PauseIcon from './assets/pauseIcon.inline.svg';
import { PLAYLIST_SECTION_VARIABLES } from './PlaylistColumnSection';

import { useNonconcurrentPlay } from 'utils/audioPlayer/useNonconcurrentPlay';
import { Classname } from 'utils/types';

interface IPlaylists extends Classname {
  title: string;
  playlists: {
    track: string;
    name: string;
    thumbnail: Pick<GatsbyImageProps, 'image' | 'alt'>;
  }[];
}

const Container = styled.div`
  ${({ theme: { colors, mq, spacings } }) => css`
    display: grid;
    grid-template-rows: auto 1fr;
    row-gap: 2.75rem;
    padding: 3rem 2rem;
    filter: drop-shadow(1rem 0.5rem 5.75rem ${colors.black}66);

    ${mq.md} {
      filter: initial;
      row-gap: 2.5rem;
      padding: 2rem ${spacings.padding} 3.75rem;
    }
  `}
`;

const Content = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;

    ${mq.md} {
      row-gap: 1.375rem;
    }
  `}
`;

const Playlist = styled.div`
  display: flex;
  column-gap: 1rem;
  align-items: center;
`;

const Title = styled.span`
  ${({ theme: { tp } }) => css`
    line-height: 1.5;
    color: var(${PLAYLIST_SECTION_VARIABLES.playlistsTextColor});
    font-size: 3.125rem;

    ${tp.useFont(fonts => fonts.Poppins.bold)};
  `}
`;

const ThumbnailWrapper = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    width: 8.75rem;
    height: 8.75rem;

    ${mq.md} {
      width: 5.5rem;
      height: 5.5rem;
    }
  `}
`;
const Thumbnail = styled(GatsbyImage)`
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;
  border-radius: 0.75rem;
  width: 100%;
  height: 100%;
  filter: grayscale(1);
`;

const Name = styled.span`
  ${({ theme: { mq, tp } }) => css`
    opacity: 0.9;
    line-height: 1.5;
    letter-spacing: 0.01em;
    color: var(${PLAYLIST_SECTION_VARIABLES.playlistsTextColor});
    font-size: 2.125rem;

    ${tp.useFont(fonts => fonts.Poppins.semiBold)};

    ${mq.md} {
      font-size: 1.3125rem;
    }
  `}
`;

const StyledAudioPlayer = styled(AudioPlayer)`
  &.rhap_container {
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
    z-index: 1;
    box-shadow: initial;
    background-color: transparent;
    padding: initial;
    height: 100%;
  }

  .rhap_progress-section {
    width: 100%;
  }

  .rhap_main-controls {
    width: 100%;
    height: 100%;
  }

  .rhap_play-pause-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .rhap_controls-section {
    display: none;
  }
`;

const Playlists: React.FC<IPlaylists> = ({ className, title, playlists }) => {
  const { getTrackPlayerRef, composeOnPlay } = useNonconcurrentPlay();

  return (
    <Container className={className}>
      <Title>{title}</Title>
      <Content>
        {playlists.map(({ track, name, thumbnail }, index) => {
          const key = `${name}${track}${index}`;

          return (
            <Playlist key={key}>
              <ThumbnailWrapper>
                <Thumbnail {...thumbnail} />
                <StyledAudioPlayer
                  src={track}
                  ref={getTrackPlayerRef(key)}
                  autoPlay={false}
                  customProgressBarSection={[RHAP_UI.MAIN_CONTROLS]}
                  customAdditionalControls={[]}
                  customControlsSection={[]}
                  customVolumeControls={[]}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  customIcons={{
                    play: <div />,
                    pause: <PauseIcon />,
                  }}
                  onPlay={composeOnPlay(index)}
                />
              </ThumbnailWrapper>
              <Name>{name}</Name>
            </Playlist>
          );
        })}
      </Content>
    </Container>
  );
};

export const desktopPlaylistColumnPlaylistsFragment = graphql`
  fragment SolutionDesktopPlaylistColumnPlaylistsData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionsPlaylistColumn {
      slice_type

      items {
        solution_playlist_column_track_thumbnail {
          gatsbyImageData
          alt
        }
        solution_playlist_column_track_name
        solution_playlist_column_track {
          url
        }
      }
    }
  }
`;

export default Playlists;
